<template>
  <div v-if="canViewTypeCategory">
    <underlying-options
      :is-mop="isMop"
      :title="title"
      :value="payload"
      :kid-type="kidType"
      :n-of-fund-category-options="nOfFundCategoryOptions"
      :n-of-fund-category-options-kiid="nOfFundCategoryOptionsKiid"
      :n-of-underlying-type-options="nOfUnderlyingTypeOptions"
      :n-of-underlying-type-options-kiid="nOfUnderlyingTypeOptionsKiid"
      @underlying-type-click="onUnderlyingTypeClick"
    ></underlying-options>
    <custom-modal :id="modalId" :value="modalValue" @on-click-table-row="onClickTableRow"></custom-modal>
  </div>
</template>

<script>
// "Opzioni di investimento" (profile), IBIPs-only, `KpiKid` tab (a structurally unrelated
// concept from IC's own Underlyings). Uses
// components/ProductDetails/UnderlyingOptions.vue (prop-driven, no Vuex/parent coupling).
//
// SOP branch: `InvestmentOptionsProfilePayload.{underlyingType, fundCategory, isin}` matches
// UnderlyingOptions.vue's own `value.{underlyingType, fundCategory, isin}` reads field-for-field
// (neither goes through the field-catalog projector system, so both serialize the enum fields as
// plain numbers by default — no JsonStringEnumConverter registered anywhere in the app —
// matching the `$t('search.filterSelectValues.optionUnderlyingType.' + code)` string
// concat the component does) — passed straight through, no adapter needed.
//
// MOP branch: `payload.distribution.{sids,kiids}` (each `{id, name, fundCategory, underlyingType,
// isin?, srri?}`) is turned into 4 count-per-category arrays (fund category and underlying type,
// for SIDs and KIIDs), ordered by Object.keys(filterSelectValues.
// optionFundCategory/optionUnderlyingType) — `filterSelectValues` itself comes from the app-wide
// Vue.mixin (main.js). Kiid counts null-coerce a zero count (`=== 0 ? null : count`).
//
// `canViewTypeCategory` permission gate: a FE-only permission with no server-side equivalent
// (InvestmentOptionsProfile's required_permission is NULL). Unlike TargetMarket (its own
// top-level tab, gated at the page level in ProductDetails.vue's productTabs()), this is one
// SECTION among several siblings inside KpiKid — gating it here, self-contained, is the natural
// granularity match rather than teaching the page/tab-filtering layer about per-section permissions.
// See ProductDetails.vue's own canViewTypeCategory computed (same pattern as canViewTargetMarket).
//
// Chart-bar-click modal — a searchable popup listing every SID/KIID behind the clicked bar. Uses
// components/Lib/CustomModal.vue (prop-driven, no Vuex/parent coupling) —
// bootstrap-vue's global `$bvModal.show(id)`/`hide(id)` API, so any component can
// drive it as long as it renders a `<custom-modal :id="...">` somewhere; `modalId` is this
// component's own id.
//
// UnderlyingOptions.vue's chart click emits `{ currentTabSelected, underlyingType: <the clicked
// bar's LABEL text>, optionUnderlyingKey: null, type: 'SID'|'KIID' }` (its own `investmentOptions`
// computed, `plotOptions.series.point.events.click`) — `onUnderlyingTypeClick` resolves
// `optionUnderlyingKey` (the numeric code the grouped-by-key maps below are keyed on) by reverse
// look-up in `filterSelectValues.optionFundCategory`/`optionUnderlyingType` (label → code) — the
// same numeric-keyed dictionaries `nOfFundCategoryOptions`/etc. below read.
//
// `groupedByFundCategory`/`groupedByUnderlyingType` (Sid and Kiid each) are grouped straight from
// `payload.distribution.sids`/`.kiids` (already available as `this.sids`/`this.kiids` below), each
// item keeping its own `id` (InvestmentOptionDistributionDto.InvestmentOption.Id) so
// `onClickTableRow` can call `selectInvestmentOption(id, name)` directly, without re-resolving the
// id by NAME (a SID and a KIID list are kept apart, so the clicked row's kind is known).
import CustomModal from '../../Lib/CustomModal'
import UnderlyingOptions from '../../ProductDetails/UnderlyingOptions'

const KID_TYPE_NUMBER_BY_NAME = { Standard: 0, Pure: 1, Hybrid: 2 }

function groupBy(list, key, mapItem) {
  return list.reduce((acc, item) => {
    const groupKey = item[key]
    acc[groupKey] = acc[groupKey] || []
    acc[groupKey].push(mapItem(item))
    return acc
  }, {})
}

export default {
  name: 'investment-options-profile-section',
  components: {
    UnderlyingOptions,
    CustomModal,
  },
  inject: ['detailPage'],
  props: {
    payload: { type: Object, required: true },
  },
  data() {
    return {
      modalId: 'investmentOptionsProfileModal',
      modalContent: null,
    }
  },
  computed: {
    canViewTypeCategory() {
      return this.detailPage.canViewTypeCategory
    },
    kidType() {
      return KID_TYPE_NUMBER_BY_NAME[this.detailPage.characteristicField('kidType')]
    },
    isMop() {
      return this.payload.distribution != null
    },
    title() {
      return this.isMop ? this.$t('productDetails.mopLabels.options') : this.$t('common.option')
    },
    sids() {
      return (this.payload.distribution && this.payload.distribution.sids) || []
    },
    kiids() {
      return (this.payload.distribution && this.payload.distribution.kiids) || []
    },
    nOfFundCategoryOptions() {
      return Object.keys(this.filterSelectValues.optionFundCategory).map(
        (key) => this.sids.filter((el) => el.fundCategory == key).length
      )
    },
    nOfFundCategoryOptionsKiid() {
      return Object.keys(this.filterSelectValues.optionFundCategory).map((key) => {
        const count = this.kiids.filter((el) => el.fundCategory == key).length
        return count === 0 ? null : count
      })
    },
    nOfUnderlyingTypeOptions() {
      return Object.keys(this.filterSelectValues.optionUnderlyingType).map(
        (key) => this.sids.filter((el) => el.underlyingType == key).length
      )
    },
    nOfUnderlyingTypeOptionsKiid() {
      return Object.keys(this.filterSelectValues.optionUnderlyingType).map((key) => {
        const count = this.kiids.filter((el) => el.underlyingType == key).length
        return count === 0 ? null : count
      })
    },
    groupedSidsByFundCategory() {
      return groupBy(this.sids, 'fundCategory', (sid) => ({
        id: sid.id,
        optionName: sid.name,
        issuerName: this.detailPage.response.issuerName,
      }))
    },
    groupedSidsByUnderlyingType() {
      return groupBy(this.sids, 'underlyingType', (sid) => ({
        id: sid.id,
        optionName: sid.name,
        issuerName: this.detailPage.response.issuerName,
      }))
    },
    groupedKiidsByFundCategory() {
      return groupBy(this.kiids, 'fundCategory', (kiid) => this.mapKiidItem(kiid))
    },
    groupedKiidsByUnderlyingType() {
      return groupBy(this.kiids, 'underlyingType', (kiid) => this.mapKiidItem(kiid))
    },
    isModalKiid() {
      return this.modalContent != null && this.modalContent.type.toLowerCase() === 'kiid'
    },
    modalItems() {
      if (this.modalContent == null) {
        return []
      }
      const byFundCategory = this.modalContent.currentTabSelected === 1
      const source = byFundCategory
        ? this.isModalKiid ? this.groupedKiidsByFundCategory : this.groupedSidsByFundCategory
        : this.isModalKiid ? this.groupedKiidsByUnderlyingType : this.groupedSidsByUnderlyingType
      return source[this.modalContent.optionUnderlyingKey] || []
    },
    modalFields() {
      return this.isModalKiid
        ? [
            { key: 'isin', label: this.$t('productDetails.kiid.isin'), sortable: true },
            { key: 'optionName', label: this.$t('common.option'), sortable: true },
          ]
        : [
            { key: 'issuerName', label: this.$t('search.resultTable.issuer'), sortable: true },
            { key: 'optionName', label: this.$t('common.option'), sortable: true },
          ]
    },
    modalValue() {
      if (this.modalContent == null) {
        return { title: '', subtitle: '', subHeader: { number: 0, label: '' }, otherInfo: {}, placeholder: '', items: [], fields: [] }
      }
      return {
        title: this.detailPage.response.name,
        subtitle: '',
        subHeader: { number: this.modalItems.length, label: this.$t('common.options') },
        otherInfo: {
          label1: (this.modalContent.currentTabSelected === 0 ? this.$t('common.typology') : this.$t('common.category')) + ': ',
          label2: this.modalContent.underlyingType,
        },
        placeholder: this.$t('common.searchBarPlaceholder'),
        items: this.modalItems,
        fields: this.modalFields,
        labelDisplayClass: ['d-inline'],
      }
    },
  },
  methods: {
    mapKiidItem(kiid) {
      return {
        id: kiid.id,
        isin: kiid.isin,
        optionName: kiid.name === kiid.isin ? this.$t('common.notAvailable') : kiid.name,
      }
    },
    onUnderlyingTypeClick(params) {
      const currentLocale = this.filterSelectValues
      const dictionary = params.currentTabSelected === 1 ? currentLocale.optionFundCategory : currentLocale.optionUnderlyingType
      const optionUnderlyingKey = Object.keys(dictionary).find((key) => dictionary[key] === params.underlyingType)
      this.modalContent = { ...params, optionUnderlyingKey }
      this.$bvModal.show(this.modalId)
    },
    onClickTableRow(item) {
      this.detailPage.selectInvestmentOption(item.id, item.optionName)
      this.$bvModal.hide(this.modalId)
    },
  },
}
</script>
